import {
  DesktopDropdownContent,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
  DesktopIconButton,
  DesktopNavButton,
  DesktopSectionLabel,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronDown, ChevronRight, ListFilter, LoaderCircle, Search } from 'lucide-react';
import {
  type ComponentProps,
  type ComponentType,
  Fragment,
  type HTMLAttributes,
  type ReactNode,
  useState,
} from 'react';
import {
  SIDEBAR_SEARCH_SHORTCUT,
  setSidebarSearchOpen,
  useSidebarSearch,
} from '@/lib/desktop-shell/sidebar-search';
import { commandLabel, useShortcutOverrides } from '@/lib/settings-page/keyboard-shortcuts';

/*
 * The building blocks of the shell sidebar, shared by the conversations, the library pages and
 * apps so every sidebar reads and behaves the same: a header row with search, filter
 * and actions, labelled or collapsible sections, items, "Show more" and empty states. Search is
 * the page's ⌘K finder (`lib/desktop-shell/sidebar-search`), which the header's button opens.
 */

type SidebarIcon = ComponentType<{ className?: string }>;

/** A choice the header's filter menu offers (how to organize or which kind to show). */
export type SidebarFilter = {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
};

/** How a collection sidebar lays out its items: under their groups, or in one list. */
export type SidebarOrganize = 'grouped' | 'list';
/** How a collection sidebar orders its items. */
export type SidebarSort = 'recent' | 'name';

/**
 * The Organize choice every collection sidebar offers (conversations, artifacts, plugin
 * pages): by its groups ("By project", "By status") or in one list.
 */
export function organizeFilter(
  value: SidebarOrganize,
  onChange: (value: SidebarOrganize) => void,
  groupNoun = 'group',
): SidebarFilter {
  return {
    label: 'Organize',
    value,
    options: [
      { value: 'grouped', label: `By ${groupNoun}` },
      { value: 'list', label: 'In one list' },
    ],
    onChange: (next) => onChange(next === 'list' ? 'list' : 'grouped'),
  };
}

/** The Sort choice every collection sidebar offers. */
export function sortFilter(
  value: SidebarSort,
  onChange: (value: SidebarSort) => void,
): SidebarFilter {
  return {
    label: 'Sort',
    value,
    options: [
      { value: 'recent', label: 'Recent' },
      { value: 'name', label: 'Name' },
    ],
    onChange: (next) => onChange(next === 'name' ? 'name' : 'recent'),
  };
}

/**
 * Orders items: most recent first (items without a date keep their order, after dated ones), or
 * by name.
 */
export function sortSidebarEntries<T>(
  entries: readonly T[],
  sort: SidebarSort,
  { name, updatedAt }: { name: (entry: T) => string; updatedAt: (entry: T) => number | undefined },
): T[] {
  const indexed = entries.map((entry, index) => ({ entry, index }));
  indexed.sort((left, right) => {
    if (sort === 'name')
      return (
        name(left.entry).localeCompare(name(right.entry), undefined, { sensitivity: 'base' }) ||
        left.index - right.index
      );
    const leftAt = updatedAt(left.entry);
    const rightAt = updatedAt(right.entry);
    if (leftAt !== undefined && rightAt !== undefined && leftAt !== rightAt)
      return rightAt - leftAt;
    if ((leftAt === undefined) !== (rightAt === undefined)) return leftAt === undefined ? 1 : -1;
    return left.index - right.index;
  });
  return indexed.map(({ entry }) => entry);
}

/** Where a sidebar's Organize and Sort choices are kept (`useSidebarArrangement`). */
function sidebarArrangementStorageKey(key: string): string {
  return `crowdex.sidebar.arrangement.${key}`;
}

/** A sidebar's Organize and Sort choices, remembered on this device per sidebar. */
export function useSidebarArrangement(
  key: string,
  defaults: { organize?: SidebarOrganize; sort?: SidebarSort } = {},
) {
  const storageKey = sidebarArrangementStorageKey(key);
  const read = (): { organize: SidebarOrganize; sort: SidebarSort } => {
    const fallback = { organize: defaults.organize ?? 'grouped', sort: defaults.sort ?? 'recent' };
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as {
        organize?: unknown;
        sort?: unknown;
      } | null;
      return {
        organize:
          stored?.organize === 'list' || stored?.organize === 'grouped'
            ? stored.organize
            : fallback.organize,
        sort: stored?.sort === 'name' || stored?.sort === 'recent' ? stored.sort : fallback.sort,
      };
    } catch {
      return fallback;
    }
  };
  const [arrangement, setArrangement] = useState(read);
  const [loadedKey, setLoadedKey] = useState(storageKey);
  if (loadedKey !== storageKey) {
    setLoadedKey(storageKey);
    setArrangement(read());
  }
  const update = (next: Partial<{ organize: SidebarOrganize; sort: SidebarSort }>) => {
    const value = { ...arrangement, ...next };
    setArrangement(value);
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Storage may be unavailable; the choice then lasts for this session.
    }
  };
  return {
    ...arrangement,
    setOrganize: (organize: SidebarOrganize) => update({ organize }),
    setSort: (sort: SidebarSort) => update({ sort }),
  };
}

/**
 * The sidebar's title row: the title, then search (the page's ⌘K finder, when it has one), the
 * filter menu (one choice per filter) and the page's own actions (icon buttons).
 */
export function SidebarHeader({
  title,
  busy = false,
  filter,
  children,
}: {
  title: ReactNode;
  busy?: boolean;
  filter?: SidebarFilter | readonly SidebarFilter[];
  children?: ReactNode;
}) {
  // Its labels show the user's keys.
  useShortcutOverrides();
  const search = useSidebarSearch().source;
  const filters = filter === undefined ? [] : 'value' in filter ? [filter] : filter;
  // The first option of each filter is its default; the button stands out once one changes.
  const filtered = filters.some((entry) => entry.value !== entry.options[0]?.value);
  const menuLabel = filters.length === 1 ? (filters[0]?.label ?? 'Filter') : 'Filter';
  return (
    <div className="mb-2 flex h-8 items-center px-3">
      <p className="min-w-0 flex-1 truncate text-muted-foreground">{title}</p>
      {busy && (
        <LoaderCircle
          aria-label="Loading"
          className="mr-1 size-3.5 animate-spin text-muted-foreground"
        />
      )}
      {search !== null && (
        <DesktopIconButton
          aria-label={search.title}
          className="size-7"
          onClick={() => setSidebarSearchOpen(true)}
          title={`${search.title} (${commandLabel('search', SIDEBAR_SEARCH_SHORTCUT)})`}
          type="button"
        >
          <Search className="size-3.5" />
        </DesktopIconButton>
      )}
      {filters.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <DesktopIconButton
              aria-label={menuLabel}
              className={cn('size-7', filtered && 'text-foreground')}
              title={menuLabel}
              type="button"
            >
              <ListFilter className="size-3.5" />
            </DesktopIconButton>
          </DropdownMenuTrigger>
          <DesktopDropdownContent align="start" className="w-52" side="right">
            {filters.map((entry, index) => (
              <Fragment key={entry.label}>
                {index > 0 && <DropdownMenuSeparator />}
                <DesktopDropdownLabel>{entry.label}</DesktopDropdownLabel>
                <DropdownMenuRadioGroup onValueChange={entry.onChange} value={entry.value}>
                  {entry.options.map((option) => (
                    <DesktopDropdownRadioItem key={option.value} value={option.value}>
                      {option.label}
                    </DesktopDropdownRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </Fragment>
            ))}
          </DesktopDropdownContent>
        </DropdownMenu>
      )}
      {children}
    </div>
  );
}

/** A labelled section of items. */
export function SidebarSection({
  label,
  count,
  children,
}: {
  label?: ReactNode;
  count?: number;
  children: ReactNode;
}) {
  return (
    <section className="mb-4">
      {label !== undefined && (
        <DesktopSectionLabel className="flex items-center gap-1.5 px-3 pb-1.5">
          <span className="min-w-0 truncate">{label}</span>
          {count !== undefined && <span className="tabular-nums">{count}</span>}
        </DesktopSectionLabel>
      )}
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

/** A collapsible group of items: a project of the conversations, a plugin's group. */
export function SidebarGroup({
  label,
  icon: Icon,
  count,
  trailing,
  action,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  wrapRow,
  rowProps,
  sectionProps,
  children,
}: {
  label: ReactNode;
  icon?: SidebarIcon;
  count?: number;
  /** Shown at the end of the row (a pin). */
  trailing?: ReactNode;
  /** A button shown on hover, after the row (new conversation). */
  action?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Wraps the row, for a hover card. */
  wrapRow?: (row: ReactNode) => ReactNode;
  /** Pointer handlers and hints of the row's toggle (reordering by drag). */
  rowProps?: Pick<ComponentProps<'button'>, 'className' | 'onPointerDown' | 'title'> & {
    /** Ignores the click that ends a drag. */
    shouldIgnoreClick?: () => boolean;
  };
  sectionProps?: HTMLAttributes<HTMLElement> & Record<`data-${string}`, string>;
  children: ReactNode;
}) {
  const [ownOpen, setOwnOpen] = useState(defaultOpen);
  const open = controlledOpen ?? ownOpen;
  const { shouldIgnoreClick, className: rowClassName, ...buttonProps } = rowProps ?? {};
  const row = (
    <div className="group flex items-center rounded-md transition-colors hover:bg-accent/55 focus-within:bg-accent/55">
      <button
        aria-expanded={open}
        className={cn(
          'type-ui flex min-w-0 flex-1 items-center gap-2 px-3.5 py-2 text-left text-foreground/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          rowClassName,
        )}
        {...buttonProps}
        onClick={() => {
          if (shouldIgnoreClick?.()) return;
          const next = !(controlledOpen ?? ownOpen);
          if (controlledOpen === undefined) setOwnOpen(next);
          onOpenChange?.(next);
        }}
        type="button"
      >
        {open ? (
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        {Icon && <Icon className="size-3.5 shrink-0 text-muted-foreground" />}
        <span className="min-w-0 flex-1 truncate font-normal">{label}</span>
        {count !== undefined && <SidebarDetail>{count}</SidebarDetail>}
        {trailing}
      </button>
      {action}
    </div>
  );
  return (
    <section {...sectionProps} className={cn('mb-4', sectionProps?.className)}>
      {wrapRow ? wrapRow(row) : row}
      {open && <div className="ml-[26px] space-y-0.5">{children}</div>}
    </section>
  );
}

/** One destination of a sidebar: a conversation, a settings section, an artifact. */
export function SidebarItem({
  active = false,
  icon: Icon,
  label,
  detail,
  trailing,
  onSelect,
  className,
  ...props
}: Omit<ComponentProps<typeof DesktopNavButton>, 'children' | 'onClick' | 'onSelect'> & {
  icon?: SidebarIcon;
  label: ReactNode;
  /** A count, an age or a short status, on the right. */
  detail?: ReactNode;
  /** Status icons on the right, in place of `detail`. */
  trailing?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <DesktopNavButton
      active={active}
      className={cn('h-8 rounded-md px-2.5 py-0', className)}
      data-sidebar-item
      density="compact"
      onClick={onSelect}
      {...props}
    >
      {Icon && <Icon className="size-3.5 shrink-0 text-muted-foreground" />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {detail !== undefined && detail !== null && <SidebarDetail>{detail}</SidebarDetail>}
      {trailing !== undefined && (
        <span className="flex shrink-0 items-center gap-1.5">{trailing}</span>
      )}
    </DesktopNavButton>
  );
}

function SidebarDetail({ children }: { children: ReactNode }) {
  return (
    <span className="type-caption max-w-[45%] shrink-0 truncate text-muted-foreground/65 tabular-nums">
      {children}
    </span>
  );
}

/** A short note in place of items (nothing yet). */
export function SidebarEmpty({ children }: { children: ReactNode }) {
  return <p className="type-caption px-3 py-1 text-muted-foreground">{children}</p>;
}

export function SidebarError({ children }: { children: ReactNode }) {
  return (
    <p className="type-caption px-3 pb-2 text-destructive" role="alert">
      {children}
    </p>
  );
}
